import React from "react";
import { Calendar, Clock, User, FileText, CheckCircle, XCircle, AlertCircle, Shield, Paperclip } from "lucide-react";

const LeaveView = ({ data }) => {
  if (!data) {
    return null;
  }

  const leave = Array.isArray(data) ? data[0] : data;

  const getStatusConfig = (status) => {
    switch (status) {
      case "APPROVED":
        return {
          color: "bg-emerald-100 text-emerald-700 border-emerald-200",
          icon: <CheckCircle size={16} />,
          label: "Approuvé"
        };
      case "REJECTED":
        return {
          color: "bg-red-100 text-red-700 border-red-200",
          icon: <XCircle size={16} />,
          label: "Refusé"
        };
      case "PENDING_MANAGER":
      case "PENDING_HR":
        return {
          color: "bg-yellow-100 text-yellow-700 border-yellow-200",
          icon: <AlertCircle size={16} />,
          label: "En attente"
        };
      default:
        return {
          color: "bg-gray-100 text-gray-700 border-gray-200",
          icon: <Clock size={16} />,
          label: status || "Inconnu"
        };
    }
  };

  const statusConfig = getStatusConfig(leave.status);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-md">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Demande de congé</h3>
            {leave.employee_name && (
              <p className="text-sm text-gray-600">{leave.employee_name}</p>
            )}
          </div>
        </div>
        <span
          className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center space-x-2 border ${statusConfig.color}`}
        >
          {statusConfig.icon}
          <span>{statusConfig.label}</span>
        </span>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {leave.type && (
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl">
            <FileText className="w-5 h-5 text-blue-600" />
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                Type de congé
              </span>
              <p className="text-sm font-semibold text-gray-900">{leave.type}</p>
            </div>
          </div>
        )}
        
        {leave.duration_days && (
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl">
            <Clock className="w-5 h-5 text-purple-600" />
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                Durée
              </span>
              <p className="text-sm font-semibold text-gray-900">{leave.duration_days} jour(s)</p>
            </div>
          </div>
        )}

        {leave.start_date && (
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl">
            <Calendar className="w-5 h-5 text-emerald-600" />
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                Date de début
              </span>
              <p className="text-sm font-semibold text-gray-900">
                {new Date(leave.start_date).toLocaleDateString("fr-FR", {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        )}

        {leave.end_date && (
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl">
            <Calendar className="w-5 h-5 text-teal-600" />
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                Date de fin
              </span>
              <p className="text-sm font-semibold text-gray-900">
                {new Date(leave.end_date).toLocaleDateString("fr-FR", {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        )}

        {leave.approver_name && (
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl">
            <Shield className="w-5 h-5 text-orange-600" />
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                Validateur
              </span>
              <p className="text-sm font-semibold text-gray-900">{leave.approver_name}</p>
            </div>
          </div>
        )}

        {leave.attachment_path && (
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl">
            <Paperclip className="w-5 h-5 text-pink-600" />
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                Pièce jointe
              </span>
              <p className="text-sm font-semibold text-gray-900 truncate">
                {leave.attachment_original_name || "Fichier joint"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Reason */}
      {leave.reason && (
        <div className="mt-4 p-4 bg-gray-50 rounded-xl">
          <div className="flex items-start space-x-3">
            <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
            <div className="flex-1">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                Motif
              </span>
              <p className="text-sm text-gray-900">{leave.reason}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveView;
